import { Server } from '@modelcontextprotocol/sdk/server/index.js';
import { StdioServerTransport } from '@modelcontextprotocol/sdk/server/stdio.js';
import { CallToolRequestSchema, ListToolsRequestSchema } from '@modelcontextprotocol/sdk/types.js';
import { AdapterRegistry } from '../analysis/adapter-registry.js';
import { ZilAdapter } from '../analysis/zil/index.js';
import { createLogger } from '../logging/index.js';
import { createLazyServices, destroyServices, type ServiceContainer } from '../services/index.js';
import { handleExecute } from './handlers/execute.handler.js';
import { tools } from './handlers/index.js';
import { ExecuteArgsSchema } from './schemas/index.js';
import type { MCPServerOptions } from './types.js';

const logger = createLogger('mcp-server');

// Register built-in language adapters
const registry = AdapterRegistry.getInstance();
if (!registry.hasExtension('.zil')) {
  registry.register(new ZilAdapter());
}

/**
 * Create MCP server with pre-initialized services.
 *
 * Services are initialized ONCE at server startup and reused for all tool calls.
 * This reduces per-call latency from 1-15s to <500ms.
 */
// eslint-disable-next-line @typescript-eslint/no-deprecated
export function createMCPServer(options: MCPServerOptions, services: ServiceContainer): Server {
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const server = new Server(
    {
      name: 'bluera-knowledge',
      version: '1.0.0',
    },
    {
      capabilities: {
        tools: {},
      },
    }
  );

  // List available tools - consolidated from 10 tools to 3 for reduced context overhead
  server.setRequestHandler(ListToolsRequestSchema, async () => {
    // Dynamically inject store names into search description for better activation
    let storeHint = '';
    try {
      const stores = await services.store.list();
      if (stores.length > 0) {
        const cap = 3;
        const names = stores
          .slice(0, cap)
          .map((s) => s.name)
          .join(', ');
        const overflow = stores.length > cap ? ` (+${String(stores.length - cap)} more)` : '';
        storeHint = ` Currently indexed: ${names}${overflow}.`;
      }
    } catch {
      // Store listing is best-effort; description works without it
    }

    return {
      tools: [
        // Native search tool with full schema (most used, benefits from detailed params)
        {
          name: 'search',
          description: `Search indexed library source code, documentation, and API references.${storeHint} You MUST use this tool to look up library/dependency details BEFORE relying on training data or grepping node_modules/vendor directories. Use whenever a question involves a third-party library, framework, or dependency. Returns structured code units with progressive context layers.`,
          inputSchema: {
            type: 'object',
            properties: {
              query: {
                type: 'string',
                description:
                  'Search query (can include type signatures, constraints, or natural language)',
              },
              intent: {
                type: 'string',
                enum: [
                  'find-pattern',
                  'find-implementation',
                  'find-usage',
                  'find-definition',
                  'find-documentation',
                  'find-files',
                ],
                description: 'Search intent for better ranking',
              },
              mode: {
                type: 'string',
                enum: ['vector', 'fts', 'hybrid'],
                default: 'hybrid',
                description:
                  'Search mode: vector (embeddings only), fts (full-text only), hybrid (both, default)',
              },
              detail: {
                type: 'string',
                enum: ['minimal', 'contextual', 'full'],
                default: 'minimal',
                description:
                  'Context detail level: minimal (summary only), contextual (+ imports/types), full (+ complete code)',
              },
              limit: {
                type: 'number',
                default: 10,
                description: 'Maximum number of results',
              },
              stores: {
                type: 'array',
                items: { type: 'string' },
                description: 'Specific store IDs to search (optional)',
              },
              threshold: {
                type: 'number',
                description: 'Minimum normalized score (0-1). Filters out low-relevance results.',
              },
              minRelevance: {
                type: 'number',
                description:
                  'Minimum raw cosine similarity (0-1). Returns empty if no results meet threshold. Use to filter irrelevant results.',
              },
            },
            required: ['query'],
          },
        },
        // Native get_full_context tool (frequently used after search)
        {
          name: 'get_full_context',
          description:
            'Get complete code and context for a specific search result by ID. Use this after search to get full implementation details.',
          inputSchema: {
            type: 'object',
            properties: {
              resultId: {
                type: 'string',
                description: 'Result ID from previous search',
              },
              file: {
                type: 'string',
                description: 'File path to retrieve context for (alternative to resultId)',
              },
              store: {
                type: 'string',
                description: 'Store name to scope file lookup (optional, searches all if omitted)',
              },
            },
          },
        },
        // Meta-tool for store and job management (consolidates 8 tools into 1)
        {
          name: 'execute',
          description:
            "Execute store/job management commands. Use 'stores' command to check which libraries are indexed. Commands: stores, store:info, store:create, store:index, store:delete, jobs, job:status, job:cancel, stores:sync, uninstall, help, commands",
          inputSchema: {
            type: 'object',
            properties: {
              command: {
                type: 'string',
                description: 'Command to execute (e.g., "stores", "store:create", "jobs", "help")',
              },
              args: {
                type: 'object',
                description: 'Command arguments (e.g., {store: "mystore"} for store:info)',
              },
            },
            required: ['command'],
          },
        },
      ],
    };
  });

  // Handle tool calls - services are pre-initialized and reused
  server.setRequestHandler(CallToolRequestSchema, async (request) => {
    const { name, arguments: args } = request.params;
    const startTime = Date.now();

    logger.info({ tool: name, args: JSON.stringify(args) }, 'Tool invoked');

    // Use pre-initialized services (no per-call creation overhead)
    const context = { services, options };

    try {
      let result;

      // Handle execute meta-tool
      if (name === 'execute') {
        const validated = ExecuteArgsSchema.parse(args ?? {});
        result = await handleExecute(validated, context);
      } else {
        // Find handler in registry for native tools (search, get_full_context)
        const tool = tools.find((t) => t.name === name);
        if (tool === undefined) {
          throw new Error(`Unknown tool: ${name}`);
        }

        // Validate arguments with Zod
        const validated = tool.schema.parse(args ?? {});

        // Execute handler with context
        result = await tool.handler(validated, context);
      }

      const durationMs = Date.now() - startTime;
      logger.info({ tool: name, durationMs }, 'Tool completed');

      return result;
    } catch (error) {
      const durationMs = Date.now() - startTime;
      logger.error(
        {
          tool: name,
          durationMs,
          error: error instanceof Error ? error.message : String(error),
        },
        'Tool execution failed'
      );
      throw error;
    }
    // No per-call cleanup - services stay alive for server lifetime
  });

  return server;
}

/**
 * Run MCP server with lazy service initialization.
 *
 * Services are initialized ONCE at startup:
 * - Lightweight services (config, store, lance wrapper): immediate
 * - Heavy services (embeddings model): deferred until first use
 *
 * This reduces server startup from ~5s to <500ms.
 */
export async function runMCPServer(options: MCPServerOptions): Promise<void> {
  logger.info(
    {
      dataDir: options.dataDir,
      projectRoot: options.projectRoot,
    },
    'MCP server starting'
  );

  // Initialize services ONCE at startup (with lazy loading for heavy components)
  const services = await createLazyServices(options.config, options.dataDir, options.projectRoot);

  const server = createMCPServer(options, services);
  const transport = new StdioServerTransport();

  // Graceful shutdown handler
  const shutdown = async (signal: string): Promise<void> => {
    logger.info({ signal }, 'Shutdown signal received');
    try {
      await destroyServices(services);
      logger.info('Services destroyed, exiting');
    } catch (error) {
      logger.error(
        { error: error instanceof Error ? error.message : String(error) },
        'Error during shutdown'
      );
    }
    // Let the process exit naturally after cleanup
  };

  process.on('SIGINT', () => void shutdown('SIGINT'));
  process.on('SIGTERM', () => void shutdown('SIGTERM'));

  await server.connect(transport);

  logger.info('MCP server connected to stdio transport');
}

// Run the server only when this file is executed directly (not imported by CLI)
// Check if we're running as the mcp/server entry point vs being imported by index.js
const scriptPath = process.argv[1] ?? '';
const isMCPServerEntry = scriptPath.endsWith('mcp/server.js') || scriptPath.endsWith('mcp/server');

if (isMCPServerEntry) {
  const rawProjectRoot = process.env['PROJECT_ROOT'];
  if (rawProjectRoot === undefined) {
    throw new Error('PROJECT_ROOT environment variable is required');
  }
  // Expand shell variables that Claude Code doesn't expand in MCP env vars
  const projectRoot = rawProjectRoot.replace(
    /\$\{(\w+)\}/g,
    (match, name: string) => process.env[name] ?? match
  );
  runMCPServer({
    dataDir: process.env['DATA_DIR'],
    config: process.env['CONFIG_PATH'],
    projectRoot,
  }).catch((error: unknown) => {
    logger.error(
      { error: error instanceof Error ? error.message : String(error) },
      'Failed to start MCP server'
    );
    process.exit(1);
  });
}
